'use client';

import { XMarkIcon } from '@heroicons/react/24/outline';
import type { RecruiterCallHistoryItem } from '@type/recruiter-call';
import { formatBucharestDateTime } from './formatBucharestDateTime';

interface RecruiterCallTranscriptModalProps {
  open: boolean;
  call: RecruiterCallHistoryItem | null;
  onClose: () => void;
}

/**
 * Read-only modal shown from the CV-detail Call-history table's "View
 * transcript" action. Renders the transcript turns + AI analysis fields
 * inline. Falls back to a friendly placeholder for zero-transcript calls
 * (busy / no-answer / still processing).
 */
export const RecruiterCallTranscriptModal = ({
  open, call, onClose,
}: RecruiterCallTranscriptModalProps) => {
  if (!open || !call) return null;

  const transcript = call.transcript ?? null;
  const analysis = call.aiAnalysis ?? null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="recruiter-call-transcript-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div className="flex max-h-[90vh] w-[92%] max-w-2xl flex-col rounded-3xl bg-surface p-6 shadow-xl">
        <div className="flex items-start justify-between border-b border-input/40 pb-3">
          <div>
            <h3 id="recruiter-call-transcript-title" className="text-lg font-semibold text-ink">
              Transcript apel
            </h3>
            <p className="mt-1 text-xs text-ink-medium">
              {formatBucharestDateTime(call.startedAt ?? call.createdAt ?? null)}
              {call.durationSeconds !== null && call.durationSeconds !== undefined && (
                <>
                  {' · '}
                  {Math.floor((call.durationSeconds ?? 0) / 60).toString().padStart(2, '0')}
                  :
                  {((call.durationSeconds ?? 0) % 60).toString().padStart(2, '0')}
                </>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Închide"
            className="flex size-8 items-center justify-center rounded-full text-ink-medium hover:bg-surface-50"
          >
            <XMarkIcon className="size-5" />
          </button>
        </div>

        <div className="mt-4 flex-1 overflow-y-auto">
          {analysis && (
            <section className="mb-5 space-y-3 rounded-2xl bg-surface-50 p-4">
              <h4 className="text-sm font-semibold text-ink">Rezumat AI</h4>
              <p className="text-sm text-ink">{analysis.summary ?? '—'}</p>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <Field label="Interes" value={analysis.interestLevel ?? null} />
                <Field label="Disponibilitate" value={analysis.availability ?? null} />
                <Field label="Salariu dorit" value={analysis.salaryExpectation ?? null} />
                <Field label="Pași următori" value={analysis.nextSteps ?? null} />
                <Field label="Semnale de atenție" value={analysis.redFlags ?? null} className="col-span-2" />
              </dl>
            </section>
          )}

          <section className="space-y-2">
            <h4 className="text-sm font-semibold text-ink">Conversație</h4>
            {transcript ? (
              <ol className="space-y-2 text-sm">
                {(transcript.turns ?? []).map((turn, idx) => (
                  <li key={`${idx}-${turn.speaker ?? ''}`} className="flex gap-2">
                    <span className="min-w-24 shrink-0 font-semibold text-ink">
                      {turn.speaker ?? ''}
                      :
                    </span>
                    <span className="text-ink">{turn.text ?? ''}</span>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-sm italic text-ink-medium">
                Transcriptul nu este încă disponibil pentru acest apel.
              </p>
            )}
            {transcript?.summary && (
              <p className="mt-2 border-t border-input/40 pt-2 text-xs italic text-ink-medium">
                {transcript.summary}
              </p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, className }: { label: string; value: string | null | undefined; className?: string }) => (
  <div className={className}>
    <dt className="text-xs uppercase tracking-wide text-ink-medium">{label}</dt>
    <dd className="mt-1 text-ink">{value ?? '—'}</dd>
  </div>
);
